// 「在庫数量以上ご希望のお客様」の案内ページを fukuro 方式（widgets/stock-shortage が描画）にする（Issue #64）。
//
// fukuro の /page/2510 はページ本体が <div data-wu-widget="stock-shortage" data-site="1"></div> の 1 行だけで、
// 商品ページの「在庫数以上をご希望の方はこちら →」（hooks/stock-check.ts）が ?p=管理番号 を付けて飛ばしてくる。
// パックジャパンの /page/694 は旧作り（ES さんの Worker へ送る直書きフォーム <div class="zkform"><form id="zkForm">）で、
// 田口さんがページを空にして同じ <div> を置く予定。
//
// ★ ページを空にするのと hook が本番に出るのは同時にできないので、どちらが先でも壊れないようにここで吸収する:
//   - 旧フォーム（.zkform で data-wu-widget を持たないもの）と、旧ページ直書きの <style>（.zkform 系の CSS。
//     ウィジェットの同名クラス .fld/.msg/.radio/.btn と衝突する）を取り除く
//   - ウィジェットの箱が無ければ自分で置いて mount する。既にあれば（田口さんが置いた後）触らない
//   - ★ ?p= が読めないときは何もしない（ウィジェットは ?p= 無しでは描画しないので、旧フォームを消すと空になる。
//     旧ページのうちはそのまま旧フォームが出続ける。空にした後は fukuro と同じく何も出ない＝仕様）
//   - 旧ページの <script>（#zkForm に submit を束ねる）は要素ごと消えるので効かない。ウィジェットの form に id は無い
//   - 旧ページのコメント「pack-japan のテーマは freepage 内の <form> を display:none で隠す」への保険として、
//     旧 CSS と同じ `.zkform form{display:block!important}` を入れる（テーマの CSS からは該当規則を見つけられなかったが、
//     旧ページが実際にこの 1 行で動いていたので同じものを持つ）

import { mountAll } from '../shared/registry'
import { injectStyleOnce } from '../shared/style'
import { resolveProductCode } from '../widgets/stock-shortage/product-code'

/** 店 → 案内ページの pathname。hooks/stock-check.ts の INQUIRY（?p={p} 付き）と対 */
export const SHORTAGE_PAGES: Record<number, string> = {
  6: '/page/694',
}

export const WIDGET_NAME = 'stock-shortage'
const FORM_VISIBLE_CSS = '.zkform form{display:block!important}'

/** この店のこのページで引き取るか。?p= が読めないときは false（旧フォームを残す） */
export function shouldTakeOver(siteId: number | null | undefined, pathname: string, search: string): boolean {
  if (!siteId) return false
  const page = SHORTAGE_PAGES[siteId]
  if (!page || (pathname !== page && pathname !== page + '/')) return false
  return resolveProductCode({}, search) !== null
}

export interface TakeOverResult {
  removedForms: number
  removedStyles: number
  /** 箱を自分で置いたか（false = 既にあった） */
  created: boolean
}

/**
 * 旧フォームと旧 CSS を外し、ウィジェットの箱が無ければ置く。mount は呼び出し側（default）で行う。
 * host は旧フォームの親（無ければフリーページ本文 .inner_free_contents、それも無ければ body）
 */
export function takeOverShortagePage(doc: Document, siteId: number): TakeOverResult {
  const olds = Array.from(doc.querySelectorAll<HTMLElement>('.zkform:not([data-wu-widget])'))
  // ★ 自分（storefront.js）が注入した <style>（data-wu-style。ウィジェットの CSS もこの hook の CSS も .zkform を含む）は外さない
  const styles = Array.from(doc.querySelectorAll('style:not([data-wu-style])')).filter((s) => (s.textContent ?? '').includes('.zkform'))
  const existing = doc.querySelector<HTMLElement>(`[data-wu-widget="${WIDGET_NAME}"]`)

  let created = false
  if (!existing) {
    const box = doc.createElement('div')
    box.setAttribute('data-wu-widget', WIDGET_NAME)
    box.setAttribute('data-site', String(siteId))
    const first = olds[0]
    if (first?.parentNode) {
      first.parentNode.insertBefore(box, first)
    } else {
      ;(doc.querySelector('.inner_free_contents') ?? doc.body).appendChild(box)
    }
    created = true
  }
  olds.forEach((el) => el.remove())
  styles.forEach((el) => el.remove())
  return { removedForms: olds.length, removedStyles: styles.length, created }
}

export default function stockShortagePage(): void {
  const siteId = window.wu?.siteInfoId
  if (!shouldTakeOver(siteId, location.pathname, location.search) || !siteId) return
  injectStyleOnce('stock-shortage-page', FORM_VISIBLE_CSS)
  const r = takeOverShortagePage(document, siteId)
  if (r.created) mountAll(document)
}
